import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const BUDGETS = ["Under $1,000", "$1,000–2,500", "$2,500–5,000", "$5,000–10,000", "$10,000+"];
const STYLES = ["Luxury", "Comfortable", "Budget", "Adventure"];
const INTERESTS = ["Beaches", "Food & wine", "Nightlife", "Nature & hiking", "Culture & history", "Wellness & spa", "Adventure sports", "Shopping"];
const STAYS = ["Hotel", "Boutique", "Villa", "Resort", "Hostel"];
const BOOKINGS = ["Flights", "Hotels", "Transfers", "Tours", "Restaurants", "Full itinerary"];
const CONTACT_VIA = ["Email", "Phone call", "WhatsApp"];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Chips = ({ label, options, value, onChange, multi, testId }) => {
  const on = (o) => (multi ? value.includes(o) : value === o);
  const toggle = (o) => onChange(multi ? (on(o) ? value.filter((x) => x !== o) : [...value, o]) : on(o) ? "" : o);
  return (
    <div>
      <p className="text-xs text-slate-400 mb-2">{label}{multi && <span className="text-slate-600"> · pick any</span>}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button key={o} type="button" onClick={() => toggle(o)} data-testid={`${testId}-${slug(o)}`}
            className={`px-3 py-1 rounded-full text-xs border transition-colors duration-150 ${on(o) ? "bg-emerald-400 border-emerald-400 text-[#04120c]" : "border-white/10 text-slate-300 hover:border-emerald-400/50"}`}>{o}</button>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => <label className="block text-xs text-slate-400 space-y-1.5"><span>{label}</span>{children}</label>;

const blank = (date, end) => ({ start_date: date || "", end_date: end || "", flexible: false, adults: 2, children: 0, budget: "", style: "", interests: [],
  stay: [], book: [], occasion: "", dream: "", phone: "", contact_via: "Email" });

export const TripFormDialog = ({ open, onOpenChange, request, country, date, end }) => {
  const qc = useQueryClient();
  const [f, setF] = useState(() => request?.form || blank(date, end));
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(request?.form || blank(date, end)); }, [open, request, date, end]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const edit = !!request?.form;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.saveTripForm(request.request_id, { ...f, adults: Number(f.adults) || 1, children: Number(f.children) || 0 });
      qc.invalidateQueries({ queryKey: ["trip-request"] });
      toast.success(edit ? "Travel form updated" : "Thanks! We'll put together your trip and a price");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the form");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="trip-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{edit ? `Your ${country} trip` : `Tell us about your dream trip to ${country}`}</DialogTitle>
          <DialogDescription>Share your ideas and expectations. Our travel team will find everything that fits, and more, and send you a price for the whole trip.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6" data-testid="trip-form">
          <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <Field label="From"><Input type="date" value={f.start_date} onChange={(e) => set("start_date")(e.target.value)} className="[color-scheme:dark]" data-testid="trip-form-start" /></Field>
            <Field label="To"><Input type="date" value={f.end_date} min={f.start_date} onChange={(e) => set("end_date")(e.target.value)} className="[color-scheme:dark]" data-testid="trip-form-end" /></Field>
            <label className="flex items-center gap-2 text-xs text-slate-300 pb-2.5"><Switch checked={f.flexible} onCheckedChange={set("flexible")} data-testid="trip-form-flexible" /> Dates are flexible</label>
          </div>
          <div className="grid grid-cols-2 gap-3 max-w-xs">
            <Field label="Adults"><Input type="number" min={1} max={30} value={f.adults} onChange={(e) => set("adults")(e.target.value)} data-testid="trip-form-adults" /></Field>
            <Field label="Children"><Input type="number" min={0} max={30} value={f.children} onChange={(e) => set("children")(e.target.value)} data-testid="trip-form-children" /></Field>
          </div>
          <Chips label="Budget per person" options={BUDGETS} value={f.budget} onChange={set("budget")} testId="trip-form-budget" />
          <Chips label="Travel style" options={STYLES} value={f.style} onChange={set("style")} testId="trip-form-style" />
          <Chips label="What do you want from this trip?" options={INTERESTS} value={f.interests} onChange={set("interests")} multi testId="trip-form-interest" />
          <Chips label="Where would you like to stay?" options={STAYS} value={f.stay} onChange={set("stay")} multi testId="trip-form-stay" />
          <Chips label="What should we book for you?" options={BOOKINGS} value={f.book} onChange={set("book")} multi testId="trip-form-book" />
          <Field label="Special occasion (honeymoon, birthday…)"><Input value={f.occasion} maxLength={100} onChange={(e) => set("occasion")(e.target.value)} placeholder="Optional" data-testid="trip-form-occasion" /></Field>
          <Field label="Your dream version of this trip: ideas, must-dos, expectations">
            <Textarea rows={5} value={f.dream} maxLength={4000} onChange={(e) => set("dream")(e.target.value)} placeholder="Sunset dinner on a rooftop, a day on a private boat, a hike nobody knows about…" data-testid="trip-form-dream" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4 items-start">
            <Field label="Phone / WhatsApp"><Input value={f.phone} maxLength={40} onChange={(e) => set("phone")(e.target.value)} placeholder="+354 …" data-testid="trip-form-phone" /></Field>
            <Chips label="Best way to reach you" options={CONTACT_VIA} value={f.contact_via} onChange={(v) => set("contact_via")(v || "Email")} testId="trip-form-contact" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="trip-form-later">{edit ? "Cancel" : "Maybe later"}</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-400 text-[#04120c] hover:bg-emerald-300" data-testid="trip-form-submit">{saving ? "Sending…" : edit ? "Save changes" : "Send to our travel team"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
